import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import PhoneInput from "react-phone-input-2";
import { useAppSelector } from "../redux/hook";
import {
  formatNigeriaPhoneNumber,
  formatPhoneNumber,
  formatSouthAfricaPhoneNumber,
} from "../utils/lib";
import "react-phone-input-2/lib/bootstrap.css";
import { useDispatch } from "react-redux";
import { message } from "antd";
import _ from "lodash";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountRegisterInput } from "../graphql/types";
import { AccountRegisterDocument } from "./__generated__/accountRegister";
import { useMutation } from "@apollo/client";
import { notification } from "antd";
import { UpdateUserDetailsDocument } from "./__generated__/updateUserDetails";
import { RESELLER_COUNTRY, RESELLER_TOKEN } from "../utils/constants";
import { setUser } from "../redux/slice/auth.slice";
import { EyeIcon, EyeOffIcon } from "@heroicons/react/outline";
import { UserLoginDocument } from "./__generated__/userLogin";
import { signInWithPopup } from "firebase/auth";
import { auth, googleProvider } from "../utils/firebase";
import { ChevronDown, Check, Search } from "lucide-react";
import { regions, provinces, states } from "./common/CountryData";
import ghFlag from "../assets/images/gh.svg";
import ngFlag from "../assets/images/ng.svg";
import zaFlag from "../assets/images/za.svg";
import PhoneVerification from "./common/PhoneOTPVerification";

const RegisterForm = () => {
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AccountRegisterInput>();
  const location = useLocation();

  const refLink = new URLSearchParams(location.search);
  const refCode = refLink?.get("refCode");

  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState<"GH" | "NG" | "ZA" | "">("");
  const [token, setToken] = useState("");
  const [phoneError, setPhoneError] = useState(false);
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState<"Male" | "Female" | "">("");
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [referredBy, setReferredBy] = useState(refCode ?? "");
  const [hidePassword, setHidePassword] = useState(false);
  const [addDetailsInput, setAddDetailsInput] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [selectedState, setSelectedState] = useState("");
  const [stateSearchQuery, setStateSearchQuery] = useState("");
  const navigate = useNavigate();

  //mapping country variables
  const countrySubdivisions = {
    GH: regions,
    NG: states,
    ZA: provinces,
  };

  const countryToName = {
    GH: "Ghana",
    NG: "Nigeria",
    ZA: "South Africa",
  };

  const countryOptions = [
    { code: "GH", name: "Ghana", flag: ghFlag, codeNumber: "(+233)" },
    { code: "NG", name: "Nigeria", flag: ngFlag, codeNumber: "(+234)" },
    { code: "ZA", name: "South Africa", flag: zaFlag, codeNumber: "(+27)" },
  ];

  useEffect(() => {
    const el = document.getElementById("referralCode") as HTMLInputElement;
    if (refCode) {
      el.value = refCode ?? "TENDO";
    }
  }, [refCode]);

  const [registerAccount, { loading: isLoadingRegisterAccount }] = useMutation(
    AccountRegisterDocument,
    {
      onError(error) {
        notification.error({
          message: "Failed to register user",
          description: error.message,
        });
      },
      onCompleted() {
        notification.success({
          message: "Success!",
          description: "User registered successfully",
        });
        notification.success({
          message: "We've send an OTP code to verify your account",
        });
      },
    }
  );
  const [userLogin] = useMutation(UserLoginDocument, {
    variables: {
      input: {
        password,
        phone,
      },
    },
    onError(error) {
      console.log("error, login user");
    },
    onCompleted() {
      console.log("login success");
    },
  });
  const [updateUserDetails, { loading: isLoadingUpdateUserDetails }] =
    useMutation(UpdateUserDetailsDocument, {
      onError(error) {
        notification.error({
          message: "Failed to update user details",
          description: error.message,
        });
      },
      onCompleted() {
        notification.success({
          message: "Success!",
          description: "User details updated successfully",
        });
        navigate("/auth/set-goal");
      },
    });


  // Handle skip phone verification
  const handleSkip = () => {
    setShowOTPInput(false);
    setAddDetailsInput(true);
    setIsPhoneVerified(false);
  };




  const signInWithGoogle = async () => {
    try {
      const data = await signInWithPopup(auth, googleProvider);
      if (data) {
        setValue("email", data.user.email as string);
        setValue("name", data.user.displayName as string);
        setValue("social_login", true);
      }
      data.user.getIdTokenResult().then((res) => {
        setPassword(res.token);
      });
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  const onSignUpSubmit = async (data: AccountRegisterInput) => {
    data.country = country;
    data.password = password;
    data.phone = phone;
    data.state = selectedState;

    if (_.isEmpty(data) || _.isEmpty(country))
      return message.error("Form fields can not be empty", 5);

    if (["", undefined, null].includes(data?.phone)) {
      message.error("Phone number can not be empty", 5);
      return;
    }

    if (!showOTPInput && !addDetailsInput) {
      const { data: registerAccountData } = await registerAccount({
        variables: {
          input: {
            ...data,
          },
        },
      });
      if (registerAccountData && registerAccountData.accountRegister) {
        const { token, user } = registerAccountData.accountRegister;
        //! @Evans use token and user here
        localStorage.setItem(RESELLER_TOKEN, token);
        localStorage.setItem(RESELLER_COUNTRY, String(user?.country));
        setToken(String(token));
        dispatch(setUser(user));
        setShowOTPInput(true);
      }
    } else {
      //!@Evans
      if (!gender) {
        message.error("Please select your gender", 5);
        return;
      }

      await updateUserDetails({
        variables: {
          input: {
            user_id: Number(user?.id),
            dob,
            gender,
            referred_by: referredBy,
          },
        },
      });
      const { data } = await userLogin();
      if (data && data.userLogin) {
        localStorage.setItem(RESELLER_TOKEN, data?.userLogin?.token || "");
        dispatch(setUser({ ...data?.userLogin?.user }));
      }
    }
  };

  const showPassword = () => {
    setHidePassword(!hidePassword);
  };

  // Filter states based on search query
  const filteredStates = country
    ? stateSearchQuery
      ? countrySubdivisions[country]?.filter((item) =>
        item.toLowerCase().includes(stateSearchQuery.toLowerCase())
      )
      : countrySubdivisions[country]
    : [];

  return (
    <>
      <div className="border-buttonSecondary bg-[#FAFFFF] border-8 border-opacity-95 rounded-lg p-8 shadow-md shadow-gray-100 font-nunito">
        {/* Phone Verification Section */}
        {showOTPInput ? (
          <div className="">
            {/* Progress Bar for register */}
            <div className="mb-9">
              <div className="flex items-center justify-between mb-2">
                <span className="text-body2 text-[#191C1E]">Step 2 of 5</span>
                <span className="text-caption text-buttonPrimary">40%</span>
              </div>
              <div className="w-full bg-[#E5E7EB] rounded-full h-1">
                <div
                  className="bg-buttonPrimary h-1 rounded-full"
                  style={{ width: "40%" }}
                ></div>
              </div>
            </div>
            <div className="text-left mb-9">
              <h1 className="text-3xl text-center">Verify Phone </h1>
            </div>
            <div className="mb-9">
              <label htmlFor="phoneOTP" className="block text-body1 font-regular text-[#6F6F6F] mb-9">
                We’ve sent a verification code via SMS and Whatsapp to your phone number  ending with {phone.slice(-4)}.
              </label>

              <>
                <PhoneVerification
                  phone={phone}
                  initialToken={token}
                  autoStart={true}
                  onVerificationSuccess={() => {
                    setIsPhoneVerified(true);
                    setShowOTPInput(false);
                    setAddDetailsInput(true);
                  }}
                  buttonClassName="hidden"
                />
              </>
            </div>
            <button
              type="button"
              className="mt-2 w-full flex justify-center py-2 px-4  text-base font-medium rounded-md text-blueText focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              onClick={handleSkip}
            >
              Verify later
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onSignUpSubmit)}
            className="overflow-x-auto scroll-smooth"
          >
            {!addDetailsInput && (
              <>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-end">
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/auth/login");
                      }}
                      className="text-buttonPrimary font-semi text-body1 cursor-pointer"
                      href="/auth/login"
                    >
                      Login
                    </a>
                  </div>

                  {/* Progress Bar for register */}
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-body2 text-[#191C1E]">Step 1 of 5</span>
                      <span className="text-caption text-buttonPrimary">20%</span>
                    </div>
                    <div className="w-full bg-[#E5E7EB] rounded-full h-1">
                      <div
                        className="bg-buttonPrimary h-1 rounded-full"
                        style={{ width: "20%" }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex flex-col text-center">
                    <h1 className="text-blackText text-header2 font-semi">
                      Create your account
                    </h1>
                    <h1 className="text-body2 font-regular text-grayText">
                      We'll send you product ideas and selling strategies to
                      help you reach your income goals.
                    </h1>
                  </div>
                </div>

                {!watch("social_login") && (
                  <div className="space-y-6 mb-4">
                    {/* Country */}
                    <div className="relative">
                      <label className="block text-body2 font-regular text-headingText mb-1 mt-6">
                        Country
                      </label>
                      <div className="relative ">
                        <button
                          type="button"
                          onClick={() =>
                            setCountryDropdownOpen(!countryDropdownOpen)
                          }
                          className="font-regular text-grayText text-body2 w-full  pl-3 pr-2 py-3 border-2 border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
                        >
                          <span
                            className={
                              country
                                ? "text-gray-700 flex items-center gap-2"
                                : "text-gray-400"
                            }
                          >
                            {country ? (
                              <>
                                <span className="rounded-full flex items-center justify-center w-5 h-5">
                                  <img
                                    src={
                                      countryOptions.find(
                                        (c) => c.code === country
                                      )?.flag
                                    }
                                    alt={countryToName[country]}
                                    className="w-full h-full object-cover rounded-full"
                                  />
                                </span>
                                {countryToName[country]}
                              </>
                            ) : (
                              "Select your country"
                            )}
                          </span>
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        </button>

                        {countryDropdownOpen && (
                          <div className="absolute z-10 w-full mt-1">
                            <div className="bg-gray-100 rounded-2xl p-1 shadow-lg">
                              <div className="bg-white rounded-xl overflow-hidden">
                                {countryOptions.map((option) => (
                                  <button
                                    key={option.code}
                                    type="button"
                                    onClick={() => {
                                      setCountry(
                                        option.code as "GH" | "NG" | "ZA"
                                      );
                                      setCountryDropdownOpen(false);
                                      setPhone("");
                                      setSelectedState("");
                                      setValue("state", "");
                                    }}
                                    className="w-full px-5 py-3 text-left hover:bg-gray-50 flex items-center justify-between border-b border-gray-50"
                                  >
                                    {/* Left side: flag, name, and code number grouped together */}
                                    <div className="flex items-center space-x-2">
                                      <span className="w-5 h-5 rounded-full overflow-hidden">
                                        <img
                                          src={option.flag}
                                          alt={option.name}
                                          className="w-full h-full object-cover"
                                        />
                                      </span>
                                      <span className="text-textPrimary text-body1 font-regular">
                                        {option.name}
                                      </span>
                                      <span className="text-textPrimary text-body1 font-regular">
                                        {option.codeNumber}
                                      </span>
                                    </div>

                                    {/* Right side: checkmark */}
                                    {country === option.code && (
                                      <div className="rounded-full p-1 flex items-center justify-center bg-backgroundSuccess">
                                        <Check className="w-4 h-4 text-blueText" />
                                      </div>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {!watch("social_login") && (
                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="text-body2 font-regular text-headingText mb-1 mt-6"
                    >
                      Name*
                    </label>
                    <div className="mt-2 relative rounded-md shadow-sm">
                      <input
                        type="text"
                        id="name"
                        autoComplete="name"
                        className=" font-regular text-grayText text-body2 w-full  pl-3 pr-2 py-3 border-2 border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="eg: Fetty Rapcha"
                        {...register("name", { required: true })}
                      />
                    </div>
                    {errors.name?.type === "required" && (
                      <span
                        className={`block text-xs text-red-600 mt-2 font-semibold`}
                      >
                        Please enter your full legal name
                      </span>
                    )}
                  </div>
                )}

                <div className="mb-4">
                  <label
                    htmlFor="phone"
                    className="text-body2 font-regular text-headingText mb-1 mt-6"
                  >
                    Whatsapp number*
                  </label>
                  <div className="mt-2">
                    <PhoneInput
                      country={country?.toLowerCase() || "gh"}
                      onlyCountries={["gh", "ng", "za"]}
                      enableSearch={false}
                      inputStyle={{
                        fontWeight: "400",
                        color: "#6B7880",
                        fontSize: "0.85rem",
                        width: "100%",
                        paddingLeft: "3.75rem",
                        paddingRight: "0.5rem",
                        paddingTop: "0.75rem",
                        paddingBottom: "0.75rem",
                        border: "2px solid #D1D5DB",
                        borderRadius: "0.5rem",
                        background: "#fff",
                      }}
                      prefix={"+"}
                      placeholder="Enter phone number"
                      value={phone}
                      countryCodeEditable={false}
                      onChange={(value) => {
                        const dialCode = value.slice(
                          0,
                          country === "GH" ? 3 : country === "NG" ? 3 : 2
                        );

                        if (dialCode === "233") {
                          setCountry("GH");
                          formatPhoneNumber({
                            value,
                            cb: setPhone,
                            errorCb: setPhoneError,
                          });
                        } else if (dialCode === "234") {
                          setCountry("NG");
                          formatNigeriaPhoneNumber({
                            phone: value,
                            cb: setPhone,
                            errorCb: setPhoneError,
                          });
                        } else if (dialCode === "27") {
                          setCountry("ZA");
                          formatSouthAfricaPhoneNumber({
                            phone: value,
                            cb: setPhone,
                            errorCb: setPhoneError,
                          });
                        }
                      }}
                    />
                  </div>
                </div>
                {phoneError && (
                  <span className="text-red-500 mt-1 text-xs font-semibold">
                    Invalid phone number
                  </span>
                )}

                {!watch("social_login") && (
                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="text-body2 font-regular text-headingText mb-1 mt-6"
                    >
                      Your email
                    </label>
                    <div className="mt-2 relative rounded-md shadow-sm">
                      <input
                        type="email"
                        id="email"
                        autoComplete="email"
                        className="font-regular text-grayText text-body2 w-full  pl-3 pr-2 py-3 border-2 border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        {...register("email", { required: true })}
                        placeholder="eg: fettyrapcha@gmail.com"
                      />
                    </div>
                    {errors.email?.type === "required" && (
                      <span
                        className={`block text-xs text-red-600 mt-2 font-semibold`}
                      >
                        Please enter your email
                      </span>
                    )}
                  </div>
                )}

                {!watch("social_login") && (
                  <div className="mt-4">
                    <label
                      htmlFor="password"
                      className="block text-body2 font-regular text-headingText mb-1 mt-6"
                    >
                      Password*
                    </label>
                    <div className="mt-2 relative rounded-md shadow-sm">
                      <input
                        type={hidePassword ? "text" : "password"}
                        id="password"
                        className="font-regular text-grayText text-body2 w-full  pl-3 pr-2 py-3 border-2 border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter Password"
                        {...register("password", { required: true })}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <div
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                        onClick={showPassword}
                      >
                        {hidePassword ? (
                          <EyeOffIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        ) : (
                          <EyeIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                    {errors.password?.type === "required" && (
                      <span
                        className={`block text-xs text-red-600 mt-2 font-semibold`}
                      >
                        Please enter a password
                      </span>
                    )}
                  </div>
                )}
                {!watch("social_login") && (
                  <div className="relative mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {country === "GH"
                        ? "Region"
                        : country === "NG"
                          ? "State"
                          : "Province"}
                    </label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() =>
                          country && setStateDropdownOpen(!stateDropdownOpen)
                        }
                        disabled={!country}
                        className={`font-regular text-grayText text-body2 w-full  pl-3 pr-2 py-3 border-2 border-gray-300 rounded-lg text-left bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between ${!country ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                      >
                        <span
                          className={
                            selectedState ? "text-gray-700" : "text-gray-400"
                          }
                        >
                          {selectedState ||
                            `Select ${country === "GH"
                              ? "Region"
                              : country === "NG"
                                ? "State"
                                : "Province"
                            }`}
                        </span>
                        <ChevronDown className="h-5 w-5 text-gray-400" />
                      </button>

                      {stateDropdownOpen && country && (
                        <div className="absolute z-10 w-full mt-1">
                          <div className="bg-gray-100 rounded-2xl p-1 shadow-lg">
                            <div className="bg-[#FAFFFF] rounded-xl overflow-hidden">
                              {/* Search Input */}
                              <div className="p-3 bg-white sticky top-0 border-b border-gray-200">
                                <div className="relative">
                                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder={`Search ${country === "GH"
                                        ? "region"
                                        : country === "NG"
                                          ? "state"
                                          : "province"
                                      }...`}
                                    value={stateSearchQuery}
                                    onChange={(e) =>
                                      setStateSearchQuery(e.target.value)
                                    }
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  {stateSearchQuery && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setStateSearchQuery("");
                                      }}
                                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* States List */}
                              <div className="max-h-48 overflow-y-auto divide-y divide-gray-100 p-2">
                                {filteredStates && filteredStates.length > 0 ? (
                                  filteredStates.map((item) => (
                                    <button
                                      key={item}
                                      type="button"
                                      onClick={() => {
                                        setSelectedState(item);
                                        setStateDropdownOpen(false);
                                        setStateSearchQuery("");
                                        setValue("state", item);
                                      }}
                                      className="w-full bg-[#FDFEFE] border border-gray-300 rounded-xl mb-2 px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between"
                                    >
                                      <span>{item}</span>
                                      {selectedState === item && (
                                        <div className="rounded-full p-1 flex items-center justify-center bg-green-500">
                                          <Check className="w-3 h-3 text-white" />
                                        </div>
                                      )}
                                    </button>
                                  ))
                                ) : (
                                  <div className="px-4 py-8 text-center text-gray-500 text-sm">
                                    No results found for "{stateSearchQuery}"
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )} <div className="max-w-md mx-auto p-6">
                <div className="relative flex items-center py-5">
                  <div className="flex-grow border-t border-gray-300"></div>
                  <span className="flex-shrink mx-4 text-gray-500 text-sm">
                    or continue with
                  </span>
                  <div className="flex-grow border-t border-gray-300"></div>
                </div>

                <div className="flex gap-4">
                  <button className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-[#F0F4F9]">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 384 512"
                      className="w-5 h-5 text-black"
                    >
                      <path
                        fill="currentColor"
                        d="M318.7 268.6c-.3-37.4 16.7-65.7 52.4-87.3-20-29-50.1-45-90.3-48.1-38.1-3-79.6 22.3-94.9 22.3-16.1 0-52.6-21.3-81.6-21.3C44.6 134.2 0 181.3 0 279.6c0 29 5.3 58.9 15.9 89.5 14.2 40.8 65.5 140.9 119 139.2 27.7-.7 47.3-19.7 83.3-19.7 35 0 53.2 19.7 84 19.7 53.9-.8 100.3-92.2 113.8-133.1-78.4-36.9-77.7-109.4-77.3-106.6zM257.1 96.4c29.3-34.8 26.6-66.5 25.7-77.8-25.9 1.5-55.8 17.6-73 37.7-19 21.8-30.2 48.8-27.8 77.5 28.1 2.2 54.2-12.8 75.1-37.4z"
                      />
                    </svg>
                    <span className="text-blackText font-semi text-body1">
                      Apple
                    </span>
                  </button>

                  <button
                    onClick={signInWithGoogle}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md bg-[#F0F4F9]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                    >
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="text-blackText font-semi text-body1">
                      Google
                    </span>
                  </button>
                </div>
              </div>
              </>
            )}

            {addDetailsInput && (
              <>
                <div className="text-left mb-5 font-nunito">
                   {/* Progress Bar for register */}
                    <div className="mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-body2 text-[#191C1E]">Step 3 of 5</span>
                      <span className="text-caption text-buttonPrimary">60%</span>
                    </div>
                    <div className="w-full bg-[#E5E7EB] rounded-full h-1">
                      <div
                        className="bg-buttonPrimary h-1 rounded-full"
                        style={{ width: "60%" }}
                      ></div>
                    </div>
                  </div>
                  <h1 className="text-header2 font-bold text-center">
                    Other details
                  </h1>
                  <p className="text-center mb-3">Please enter your detail below</p>
                </div>
              
                <div className="mb-3">
                  <label htmlFor="number" className="block text-body2 text-grayText font-medium mb-1">
                    Date of birth
                  </label>
                  <div className=" relative rounded-md shadow-sm">
                    <input
                      type="date"
                      id="dob"
                      autoComplete="dob"
                      className="focus:ring-gray-300 focus:border-gray-300 block w-full pl-7 pr-12 py-4 text-grayText text-regular  text-body1 border-gray-100 border-2 rounded-md"
                      placeholder="12/11/2000"
                      onChange={(e) => {
                        setDob(e.target.value);
                      }}
                    />
                  </div>
                </div>
                  <div className="mb-3">
                  <label htmlFor="number" className="block text-body2 text-grayText font-medium mb-1">
                    Your gender
                  </label>
                  <select
  id="gender"
  name="gender"
  className="focus:ring-gray-300 focus:border-gray-300 block w-full pl-7 pr-12 py-4 text-greyText text-regular border-gray-100 border-2 rounded-md"
  value={gender}
  required
  onChange={(e) => setGender(e.target.value as "Male" | "Female" | "")}
>
  <option value="" disabled>
    Select your gender
  </option>
  <option value="Male">Male</option>
  <option value="Female">Female</option>
</select>
                </div>
                <div className="mb-3">
                <label htmlFor="number" className="block text-body1 text-grayText font-medium mb-1">
                Referral (Optional)
                  </label>
                  <div className="mt-2 relative rounded-md shadow-sm">
                    <input
                      type="text"
                      id="referralCode"
                      className="focus:ring-gray-300 focus:border-gray-300 block w-full pl-7 pr-12 py-4 text-greyText text-regular border-gray-100 border-2 rounded-md"
                      placeholder="Code"
                      defaultValue={refCode || ""}
                      onChange={(e) => setReferredBy(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}
            <div className="flex flex-col py-4">
              <div className="my-2 w-full">
                <button
                  type="submit"
                  className={`w-full flex justify-center py-4 px-4 border border-transparent text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 text-white ${isLoadingRegisterAccount || isLoadingUpdateUserDetails
                      ? "cursor-not-allowed bg-gray-200"
                      : "bg-blue-900"
                    }`}
                  disabled={
                    isLoadingRegisterAccount || isLoadingUpdateUserDetails
                      ? true
                      : false
                  }
                >
                  {(isLoadingRegisterAccount || isLoadingUpdateUserDetails) && (
                    <AiOutlineLoading3Quarters className="h-6 w-6 animate-spin text-body1 text-buttonPrimary mr-2" />
                  )}
                  {addDetailsInput ? "Continue" : "Next"}
                </button>
              </div>
              <div></div>
             
            </div>
          </form>
        )}
      </div>
    </>
  );
};

export default RegisterForm;
